import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

import { Box, Flex, Input, Stack, Skeleton } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  closeCreateInitiativePanelAction,
  openCreateInitiativePanelAction,
} from 'actions/createInitiativesPanelActions';
import MeshListContext from 'components/MeshList/MeshListContext';
import { openPanel } from 'components/RabbitInitiativeCreationPanel/InitiativeCreationPanelSlice';
import { closeSecondaryPanel } from 'components/RabbitInitiativeSidePanel/initiativeSecondaryPanelSlice';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import { useEmployeeGoalsPrivileges } from 'components/hooks/useEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import { selectCreateInitiativePanelData } from 'selectors/createInitiativePanelSelector';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { selectMSTeamsContext } from 'selectors/msTeamsSelector';
import { selectTaskDetails } from 'selectors/taskDetails';
import { baseTheme } from 'themes/clients/baseTheme';
import {
  checkNewCompetencyPanelEnabled,
  checkNewNavEnabled,
  checkOverviewExplanationsEnabled,
} from 'utils/helpers/featureFlags';
import { checkOneOnOneModuleEnabled } from 'utils/helpers/moduleAccess';

import { PlusNotOutlinedWhite } from 'assets/assetsV1/iconComponents';
import NoInitiatives from 'assets/assetsV1/illustrations/newIllustrations/noInitiatives.svg';
import NoInitiativesInsights from 'assets/assetsV1/illustrations/newIllustrations/noInitiativesInsights.svg';

import { ANALYTICS_FILTERS_KEYS, thisYear, MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
// Get QueryClient from the context
import {
  getDateRangeFromLSBasedOnEndDateStartDate,
  getMatchPathIndexFromRoutesArray,
  getPersistenceKey,
  setFiltersToLS,
} from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { localStorageKeys } from 'constants/localStorageKeys';
import { sendClickedOnAddNewButton, sendOpenedInitiativePanel } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { routes } from 'constants/routes';

import { Button } from 'uiToolkitV1/Button';
import { ChipSelect } from 'uiToolkitV1/ChipSelect';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { PageTabbedNavigation } from 'uiToolkitV1/PageTabbedNavigation';
import { Paper } from 'uiToolkitV1/Paper';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import TabbedNavigationTitle from '../Components/TabbedNavigationTitle';
import { useInitiativeTableData, useMyInitiativeTableData } from '../Hooks/dataHooks';
import AGGridTable from './AGGrid';

const TABLE_FILTERS = [
  {
    type: 'STATUS',
    value: 'ALL',
    displayValue: 'All',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'ONGOING',
    displayValue: 'On-going',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'DELAYED',
    displayValue: 'Delayed',
    operator: 'IN',
  },
  {
    type: 'STATUS',
    value: 'UNPLANNED',
    displayValue: 'Unplanned',
    operator: 'IN',
  },

  {
    displayValue: 'Completed',
    operator: 'IN',
    type: 'STATUS',
    value: 'COMPLETED',
  },
];

const RenderTable = ({
  isLoading,
  data,
  handleMarkClickButton,
  display,
  filterSelected,
  cohortDetails,
  dateRange,
  openCreateInitiativeSidePanel,
  btnRef,
  canCreateInitiativePrivilege = true,
  gridStatePersistenceKey = null,
  gridRef,
}) => {
  const { type } = useSelectedView() ?? {};

  const isMyInitiatives = type === 'MY_INITIATIVES';

  const img = isMyInitiatives ? NoInitiatives : NoInitiativesInsights;

  const isMyTeam = type === 'MY_TEAM';

  const isMyOrg = type === 'MY_ORGANIZATION';

  const isMyCompany = type === 'ALL_EMPLOYEE';

  const textToInsert = isMyTeam ? 'team' : isMyOrg ? 'organization' : isMyCompany ? 'company' : 'cohort';

  const noInitiativesHeading = isMyInitiatives
    ? 'Initiatives are activities that you complete to achieve your competencies'
    : `See all the initiatives for your ${textToInsert} in a familiar list view.`;

  const noInitiativesDescription = isMyInitiatives
    ? 'Take the initiative and get started now'
    : 'Add initiatives for them to improve your chances of achieving your goals.';

  if (isLoading) {
    return (
      <Stack>
        <Skeleton height="20px" />
        <Skeleton height="20px" />
        <Skeleton height="20px" />
      </Stack>
    );
  }
  if (data?.computedData?.length > 0) {
    return (
      <AGGridTable
        gridStatePersistenceKey={gridStatePersistenceKey}
        tableData={data?.computedData}
        onMarkClicked={handleMarkClickButton}
        display={display}
        filters={filterSelected}
        cohort={cohortDetails}
        dateRange={dateRange}
        filtersArray={TABLE_FILTERS}
        gridRef={gridRef}
      />
    );
  }
  return (
    <Flex justifyContent="center">
      <div style={{ marginTop: '60px' }}>
        <EmptyScreen
          heading={noInitiativesHeading}
          description={noInitiativesDescription}
          illustrationSrc={img}
          CTAComp={() => {
            if (canCreateInitiativePrivilege)
              return (
                <Button onClick={openCreateInitiativeSidePanel} StartIcon={PlusNotOutlinedWhite} ref={btnRef}>
                  Add New
                </Button>
              );
          }}
        />
      </div>
    </Flex>
  );
};

const getRouteHelper = ({ id, name, type }) => {
  const queryStringObj = {
    id,
    name,
    type,
  };
  return `${routes.initiative.audit}?cohort=${encodeURIComponent(JSON.stringify(queryStringObj))}`;
};

export const updatedThisYear = {
  endDate: thisYear.endDate.valueOf(),
  startDate: thisYear.startDate.valueOf(),
  label: thisYear.label,
};

const DEFAULT_FILTER = TABLE_FILTERS?.[0]?.value;

function InitiativeAudit({ dateRange: dateRangeFromProps }) {
  const location = useLocation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const [searchText, setSearchText] = useState('');
  const [employeeTeams, setEmployeeTeams] = useState(loggedInEmployee.teams ?? []);
  const [isSidePanelOpened, setSidePanelOpened] = useState(false);
  const [newTask, setNewTask] = useState(null);
  const [createNewInProgress, setCreateNewInProgress] = useState(false);
  const gridRef = useRef();
  // const cohortsFromApi = useSelector((state) => state.sidebarNav);
  const cohortDetails = useSelectedView();

  const [filterSelected, setFilterSelected] = useState([DEFAULT_FILTER]);

  const [dateRange, setDateRange] = useState(
    getDateRangeFromLSBasedOnEndDateStartDate(localStorageKeys?.rabbitInitiativeAnalyticsFilters) ?? updatedThisYear
  );

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const INITIATIVES_TYPES = useMemo(
    () => [
      {
        text: `${modifyString(moduleName, StringModifier.TITLE_CASE)} initiatives`,
        id: 'GOAL',
        enabled: () => true,
      },
      {
        text: '1:1 initiatives',
        id: 'ONE_ON_ONE',
        enabled: () => checkOneOnOneModuleEnabled(),
      },
      {
        text: 'Competency initiatives',
        id: 'COMPETENCY_CHECKIN',
        enabled: () => checkNewCompetencyPanelEnabled(),
      },
      {
        text: 'Nudge initiatives',
        id: 'NUDGE_TASK',
        enabled: () => checkOverviewExplanationsEnabled(),
      },
    ],
    []
  );

  const [selectedOneOnOneType, setSelectedOneOnOneType] = useState([INITIATIVES_TYPES[0]?.id]);

  const isMyInitiatives = cohortDetails?.type === 'MY_INITIATIVES';

  const dateRangeToUse = checkNewNavEnabled() ? dateRangeFromProps : dateRange;

  const { data: tableData, isLoading: isTableDataAwaited } = useInitiativeTableData({
    cohort: cohortDetails,
    dateRange: dateRangeToUse,
    enabled: !isMyInitiatives,
  });

  const { data: myInitiativesData, isLoading: isLoadingOKRInitiatives } = useMyInitiativeTableData({
    type: selectedOneOnOneType[0],
    dateRange: dateRangeToUse,
    enabled: cohortDetails?.type === 'MY_INITIATIVES',
  });

  const { data: oneOnOneInitiativesData, isLoading: isLoadingOneOnOneInitiatives } = useMyInitiativeTableData({
    type: (INITIATIVES_TYPES && INITIATIVES_TYPES[1]?.id) ?? null,
    dateRange: dateRangeToUse,
    enabled: cohortDetails?.type === 'MY_INITIATIVES',
  });

  const oneOnOneInitiativesMoreThanOne = oneOnOneInitiativesData?.computedData?.length > 0;

  const initiativesTypesToShow = useMemo(
    () =>
      INITIATIVES_TYPES.filter((type) => {
        if (type?.id === 'ONE_ON_ONE') {
          return type?.enabled?.() && oneOnOneInitiativesMoreThanOne;
        }
        return type?.enabled?.();
      }),
    [INITIATIVES_TYPES, oneOnOneInitiativesMoreThanOne]
  );

  const { isOpen } = useSelector(selectCreateInitiativePanelData, shallowEqual);
  const [onMarkClicked, setOnMarkClicked] = useState(false);
  const { display } = useSelector(selectTaskDetails);
  const page = useViewPageName();

  const isMsTeamsVisitor = useSelector(selectMSTeamsContext);

  const { search: queryString } = location;
  const btnRef = React.useRef();
  const dispatch = useDispatch();
  const contextValue = useMemo(
    () => ({
      dispatch,
      tasksFilters: {},
      selectedTeamsPageView: {},
      setCreateNewInProgress,
    }),
    [dispatch]
  );

  const INITIATIVE_LANDING_PAGE_TABS = [
    {
      name: 'Explore',
      route: routes.initiative.audit,
      search: `${queryString}`,
      accessFn: () => true,
    },
    {
      name: 'Card',
      route: routes.initiative.cards,
      search: `${queryString}`,
      accessFn: () => {
        if (isMobileView() || isMsTeamsVisitor || cohortDetails?.type === 'ALL_EMPLOYEE') return false;
        return true;
      },
    },
  ];

  const routesArray = INITIATIVE_LANDING_PAGE_TABS.map((tabData) => tabData.route);

  useEffect(() => {
    // display is controlling whether initiatives is being viewed/updated
    // isOpen is controlling whether add new initiatives side-panel is open/close
    // @shreyansh: shukriya
    if (!display || !isOpen) {
      queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeTableData]);
      queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeStatsData]);
    }
  }, [display, queryClient, isOpen]);

  const selectedTabRouteIndex = getMatchPathIndexFromRoutesArray({
    pathname: location.pathname,
    routesArray,
  });

  // const getCohort = useCallback(() => {
  //   const defaultCohort =
  //     cohortsFromApi?.selectedCohort ?? cohortsFromApi?.cohorts?.defaultList?.[0];
  //   let copiedCohort;
  //   try {
  //     copiedCohort = JSON.parse(JSON.stringify(defaultCohort));
  //   } catch (error) {
  //     console.log(error, 'Error parsing default');
  //   }

  //   if (copiedCohort?.['name'] === 'My Initiatives') {
  //     copiedCohort = {
  //       ...copiedCohort,
  //       id: -1,
  //       uuid: null
  //     };
  //   } else {
  //     copiedCohort = defaultCohort;
  //   }
  //   return copiedCohort;
  // }, [cohortsFromApi]);

  const { canCreateInitiativePrivilege } = useEmployeeGoalsPrivileges();
  const isInitiativeCreationAllowed = canCreateInitiativePrivilege;

  const selectedTabName = INITIATIVE_LANDING_PAGE_TABS?.[selectedTabRouteIndex]?.name ?? '';

  const handleNewInitiativeSave = useCallback((task) => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeTableData]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeStatsData]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.MyInitiativeTableData]);
  }, []);

  const closeCreateInitiativeSidePanel = useCallback(() => {
    setSidePanelOpened(false);
    dispatch(closeCreateInitiativePanelAction());
    handleNewInitiativeSave();
  }, [dispatch]);

  const openCreateInitiativeSidePanel = useCallback(() => {
    sendClickedOnAddNewButton({
      eventProperties: {
        type: 'Initiative',
        page,
      },
    });

    setSidePanelOpened(true);

    const updatedToIds = [
      {
        uuid: loggedInEmployee.id,
        ownerType: 'OWNER',
        metadata: { displayName: loggedInEmployee.displayName },
        profile: { profilePhoto: loggedInEmployee.profilePhotoSrc },
      },
    ];

    dispatch(closeSecondaryPanel()); /// // WILL CLOSE SECONDARY DRAWER ON INITIAL OPENING ON ADD NEW INITIATIVE PANEL
    sendOpenedInitiativePanel({
      eventProperties: {
        page,
        source: 'Add new button',
      },
    });
    dispatch(
      openPanel({
        toIds: updatedToIds,
      })
    );

    dispatch(
      openCreateInitiativePanelAction({
        onClose: closeCreateInitiativeSidePanel,
        onCancel: closeCreateInitiativeSidePanel,
        team: cohortDetails,
        onSave: handleNewInitiativeSave,
        selectedTimeBoundPeriod: {},
        ownerEmployee: loggedInEmployee,
        subObjective: null,
      })
    );
  }, [
    dispatch,
    setSidePanelOpened,
    loggedInEmployee,
    handleNewInitiativeSave,
    closeCreateInitiativeSidePanel,
    cohortDetails,
  ]);

  const updateDateRange = useCallback(
    (date) => {
      setDateRange(date);
      setFiltersToLS(localStorageKeys.rabbitInitiativeAnalyticsFilters, ANALYTICS_FILTERS_KEYS.dateRange, date);
    },
    [setDateRange]
  );

  const onFilterChange = (arr) => {
    setFilterSelected(arr);
  };

  const handleMarkClickButton = () => {
    setOnMarkClicked(true);
  };

  const onChangeHandler = (id) => {
    setSelectedOneOnOneType(id);
  };

  const onSearchInputChange = (e) => {
    setSearchText(e.target.value);
  };

  useEffect(() => {
    if (searchText !== null && gridRef?.current?.api) {
      gridRef?.current?.api?.setGridOption('quickFilterText', searchText);
    }
  }, [gridRef, searchText]);

  /* Grid State persistence Key */
  const isOneOnOneInitiatives = selectedOneOnOneType[0] === 'ONE_ON_ONE';
  const viewId = cohortDetails?.id;
  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.TASK;
    const tabId = null;
    const uniqueId = isOneOnOneInitiatives ? '1on1_Initiatives_Grid' : 'Goals_Initiatives_Grid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, [isOneOnOneInitiatives, viewId]);

  return (
    <MeshListContext.Provider value={contextValue}>
      {!checkNewNavEnabled() && (
        <PageTabbedNavigation
          tabsData={cohortDetails?.name === 'My Initiatives' ? [] : INITIATIVE_LANDING_PAGE_TABS}
          selectedTabName={selectedTabName}
          cohortName={cohortDetails?.name}
          title={<TabbedNavigationTitle cohortName={cohortDetails?.name} updateDateRange={updateDateRange} />}
        />
      )}
      <Box p={!checkNewNavEnabled() ? 4 : 0}>
        <Paper p={3} mb={2} overflowX="auto" boxShadow={baseTheme.shadows.paper} borderRadius="8px">
          <Flex
            style={{ marginBottom: '16px' }}
            justifyContent="space-between"
            alignItems={!isMobileView() && 'center'}
            flexWrap="wrap"
            flexDirection={isMobileView() && ' column'}
            minH="40px"
          >
            <Input
              placeholder="Search title"
              sx={{ maxWidth: '360px' }}
              value={searchText}
              onChange={onSearchInputChange}
            />
            {/* <Filters
                  filtersArray={TABLE_FILTERS}
                  filterSelected={filterSelected}
                  onFilterChange={onFilterChange}
                  defaultFilter={DEFAULT_FILTER}
                  statsData={statsData}
                  tableData={tableData}
                /> */}
            {!['ONE_ON_ONE', 'COMPETENCY_CHECKIN'].includes(selectedOneOnOneType[0]) && isInitiativeCreationAllowed && (
              <Button
                onClick={openCreateInitiativeSidePanel}
                StartIcon={PlusNotOutlinedWhite}
                ref={btnRef}
                marginTop="10px"
              >
                Add
              </Button>
            )}
          </Flex>
          {isMyInitiatives && (
            <Flex
              sx={{ marginRight: 'auto' }}
              direction={isMobileView() ? 'column' : 'row'}
              my={isMobileView() ? '10px' : '0px'}
              gap={isMobileView() ? '10px' : '0px'}
              mb={4}
            >
              <TextBodyMedium bold color="text.g3" mr={2}>
                Showing:{' '}
              </TextBodyMedium>
              <ChipSelect
                options={initiativesTypesToShow}
                onChange={onChangeHandler}
                forceMinOneSelection
                selectedIds={selectedOneOnOneType}
                multipleSelect={false}
              />
            </Flex>
          )}
          <RenderTable
            btnRef={btnRef}
            gridStatePersistenceKey={gridStatePersistenceKey}
            cohortDetails={cohortDetails}
            data={isMyInitiatives ? myInitiativesData : tableData}
            isLoading={isMyInitiatives ? isLoadingOKRInitiatives : isTableDataAwaited}
            dateRange={dateRange}
            filterSelected={filterSelected}
            display={display}
            handleMarkClickButton={handleMarkClickButton}
            openCreateInitiativeSidePanel={openCreateInitiativeSidePanel}
            canCreateInitiativePrivilege={
              !['ONE_ON_ONE', 'COMPETENCY_CHECKIN'].includes(selectedOneOnOneType[0]) && isInitiativeCreationAllowed
            }
            gridRef={gridRef}
          />
        </Paper>
      </Box>
    </MeshListContext.Provider>
  );
}

export default InitiativeAudit;
